import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  AlertTriangle, CheckCircle2, Loader2, Link2, Unlink, KeyRound, ShieldCheck, ShieldAlert,
  MonitorSmartphone, Copy, RefreshCw, ExternalLink,
} from 'lucide-react';
import {
  brokerConnectionService as svc,
  type AvailableBroker,
  type BrokerConnection,
  type ConformanceReport,
  type TerminalAgentStatus,
  type TradingEnvironment,
} from '@/services/brokerConnectionService';

/**
 * Configuration → Broker Connections.
 *
 * One screen for every integration added under the direct-broker program: store
 * credentials or run an OAuth consent, discover and pin the account orders route
 * to, prove the connection, and run the adapter conformance suite.
 *
 * Deliberately shows what each broker CANNOT do as prominently as what it can —
 * a long-only venue, a mocked sandbox, or a non-authoritative order lookup all
 * change how a strategy should be routed, and hiding that until an order fails is
 * how people lose money.
 */
export const BrokerConnectionsSettingsPage: React.FC = () => {
  const [brokers, setBrokers] = useState<AvailableBroker[]>([]);
  const [connections, setConnections] = useState<BrokerConnection[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [environment, setEnvironment] = useState<TradingEnvironment>('Paper');

  const refresh = useCallback(async () => {
    try {
      const [available, existing] = await Promise.all([svc.available(), svc.list()]);
      setBrokers(available);
      setConnections(existing);
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Failed to load broker connections');
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const connectionFor = useCallback(
    (provider: string, env: TradingEnvironment) =>
      connections.find((c) => c.provider === provider && c.environment === env) ?? null,
    [connections],
  );

  // Legacy routes (Alpaca keys, FXCM, IG) keep their own dedicated screens; this
  // page owns the generic connection model only.
  const visible = useMemo(
    () => brokers
      .filter((b) => !['Fxcm', 'Ig'].includes(b.provider))
      .filter((b) => b.environments.includes(environment))
      .sort((a, b) => a.provider.localeCompare(b.provider)),
    [brokers, environment],
  );

  return (
    <div className="space-y-3 max-w-4xl">
      <div>
        <h2 className="text-lg font-semibold">Broker Connections</h2>
        <p className="text-xs text-muted-foreground">
          Connect a brokerage directly. Credentials are encrypted at rest and are never
          returned to this page — a blank field keeps whatever is stored.
        </p>
      </div>

      <div className="inline-flex rounded-md border p-0.5 text-xs">
        {(['Paper', 'Live'] as TradingEnvironment[]).map((env) => (
          <button
            key={env}
            onClick={() => setEnvironment(env)}
            className={`px-3 py-1 rounded-[5px] font-medium transition-colors ${
              environment === env
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {env === 'Paper' ? 'Paper / sandbox' : 'Live'}
          </button>
        ))}
      </div>

      {!loaded && (
        <div className="text-xs text-muted-foreground flex items-center gap-1.5">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading brokers…
        </div>
      )}

      {error && (
        <div className="text-xs text-red-500 flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" /> {error}
        </div>
      )}

      {loaded && !error && visible.length === 0 && (
        <p className="text-xs text-muted-foreground">
          No broker in this deployment offers a {environment === 'Paper' ? 'paper/sandbox' : 'live'} environment.
        </p>
      )}

      {visible.map((broker) => (
        <BrokerCard
          // Keyed by environment as well as provider.
          //
          // With a provider-only key React kept this subtree mounted across a
          // Paper/Live switch, so every request already in flight — a terminal status
          // poll, an OAuth1 start or completion — landed afterwards and wrote the old
          // environment's data into the new environment's panel. That hid or mis-stated
          // the live switch, and discarded a fresh request-token pair. Remounting
          // discards the whole class of stale-crossing state instead of guarding each
          // call site one at a time.
          key={`${broker.provider}:${environment}`}
          broker={broker}
          environment={environment}
          connection={connectionFor(broker.provider, environment)}
          onChanged={refresh}
        />
      ))}
    </div>
  );
};

// ── One broker ───────────────────────────────────────────────────

interface BrokerCardProps {
  broker: AvailableBroker;
  environment: TradingEnvironment;
  connection: BrokerConnection | null;
  onChanged: () => Promise<void>;
}

const BrokerCard: React.FC<BrokerCardProps> = ({ broker, environment, connection, onChanged }) => {
  const isTerminalAgent = broker.connectorKind === 'TerminalAgent';

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            {isTerminalAgent
              ? <MonitorSmartphone className="h-4 w-4 text-primary" />
              : <Link2 className="h-4 w-4 text-primary" />}
            {broker.provider}
          </span>
          <StatusPill connection={connection} />
        </CardTitle>
      </CardHeader>

      <CardContent className="pt-0 space-y-3">
        <CapabilitySummary broker={broker} />

        {broker.connectorKind !== 'BrokerApi' && (
          <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-2 text-[11px] text-amber-700 dark:text-amber-400">
            {broker.provider} is an execution <strong>platform</strong>, not the broker. Your account
            stays with your own brokerage, which continues to execute every order — the connection
            records which one.
          </p>
        )}

        {broker.caveats.length > 0 && (
          <ul className="space-y-1 text-[11px] text-muted-foreground">
            {broker.caveats.map((caveat) => (
              <li key={caveat} className="flex gap-1.5">
                <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0 text-amber-500" />
                <span>{caveat}</span>
              </li>
            ))}
          </ul>
        )}

        {isTerminalAgent
          ? <TerminalAgentPanel provider={broker.provider} environment={environment} />
          : (
            <>
              <CredentialPanel
                broker={broker}
                environment={environment}
                connection={connection}
                onChanged={onChanged}
              />
              {connection?.hasCredentials && (
                <>
                  <AccountPanel
                    broker={broker}
                    environment={environment}
                    connection={connection}
                    onChanged={onChanged}
                  />
                  <VerifyPanel broker={broker} environment={environment} />
                  {/* Only where this connection's flag is what actually gates live
                      orders. LiveTradingGate reads Alpaca, FXCM and IG from their own
                      credentials, so a generic control here would neither enable nor
                      disable them while reporting that it had — the server refuses
                      those writes too, this just avoids offering the button. */}
                  {environment === 'Live' && broker.liveSwitchManagedHere && (
                    <LiveSwitch
                      provider={broker.provider}
                      enabled={connection.liveTradingEnabled}
                      onChanged={onChanged}
                    />
                  )}
                </>
              )}
            </>
          )}
      </CardContent>
    </Card>
  );
};

/**
 * OAuth 1.0a, which is a different shape from OAuth 2: the broker hands back a URL,
 * the user authorises there and is shown a verifier code to copy back. There is no
 * redirect to catch, so this is a two-step form rather than a single button.
 */
const OAuth1Panel: React.FC<{
  broker: AvailableBroker;
  environment: TradingEnvironment;
  connection: BrokerConnection | null;
  onChanged: () => void;
}> = ({ broker, environment, connection, onChanged }) => {
  const [pending, setPending] = useState<{ authorizeUrl: string; requestToken: string; requestTokenSecret: string } | null>(null);
  const [verifier, setVerifier] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  // A request-token pair is issued against ONE environment's consumer credentials.
  // BrokerCard is keyed only by provider, so switching Paper→Live keeps this panel
  // mounted and would otherwise submit the Paper handshake against the Live consumer —
  // failing outright, or worse, completing against the wrong account.
  //
  // The generation ref covers the other half: clearing state on switch is not enough
  // when a startEtrade is still in flight, because its response lands afterwards and
  // repopulates `pending` with the previous environment's tokens.
  const handshakeGen = useRef(0);
  useEffect(() => {
    handshakeGen.current += 1;
    setPending(null);
    setVerifier('');
    setFailure(null);
    // Cleared HERE rather than in the stale request's finally: that branch is skipped
    // precisely because the request is stale, so the panel would sit disabled forever
    // in the environment the user just switched to.
    setBusy(false);
  }, [environment]);

  const start = async () => {
    const mine = handshakeGen.current;
    setBusy(true);
    setFailure(null);
    try {
      const started = await svc.startEtrade(environment);
      // Dropped if the environment changed while this was in flight — those tokens
      // belong to the environment that is no longer selected.
      if (mine !== handshakeGen.current) return;
      setPending(started);
      window.open(started.authorizeUrl, '_blank', 'noopener,noreferrer');
    } catch (e) {
      if (mine !== handshakeGen.current) return;
      setFailure(e instanceof Error ? e.message : 'Could not start the authorisation');
    } finally {
      if (mine === handshakeGen.current) setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    setFailure(null);
    try {
      await svc.disconnect(broker.provider, environment);
      setPending(null);
      setVerifier('');
      onChanged();
    } catch (e) {
      setFailure(e instanceof Error ? e.message : 'Could not disconnect');
    } finally {
      setBusy(false);
    }
  };

  const complete = async () => {
    if (!pending || !verifier.trim()) return;
    setBusy(true);
    setFailure(null);
    try {
      await svc.completeEtrade(environment, {
        requestToken: pending.requestToken,
        requestTokenSecret: pending.requestTokenSecret,
        verifier: verifier.trim(),
      });
      // The request token is single-use; clear it so a second submit cannot replay it.
      setPending(null);
      setVerifier('');
      onChanged();
    } catch (e) {
      setFailure(e instanceof Error ? e.message : 'Could not complete the authorisation');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      {!pending ? (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => void start()} disabled={busy}>
            {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <ExternalLink className="mr-1 h-3.5 w-3.5" />}
            {connection?.hasCredentials ? 'Reconnect' : 'Connect'} {broker.provider}
          </Button>
          {/* This panel returns before the generic credential controls, so it has to
              carry its own disconnect - otherwise a completed OAuth1 authorisation
              leaves a stored access token with no way to remove it from the UI. */}
          {connection?.hasCredentials && (
            <Button size="sm" variant="outline" onClick={() => void disconnect()} disabled={busy}>
              <Unlink className="mr-1 h-3.5 w-3.5" /> Disconnect
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-2 rounded-md border border-primary/40 bg-primary/[0.04] p-3">
          <p className="text-xs text-muted-foreground">
            Authorise in the tab that just opened, then paste the verification code {broker.provider}
            {' '}shows you. If the tab did not open,{' '}
            <a href={pending.authorizeUrl} target="_blank" rel="noopener noreferrer" className="underline">
              open it here
            </a>.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <input
              value={verifier}
              onChange={(e) => setVerifier(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void complete(); } }}
              placeholder="Verification code"
              autoFocus
              className="w-44 rounded border bg-background px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <Button size="sm" onClick={() => void complete()} disabled={busy || !verifier.trim()}>
              {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null} Finish
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setPending(null); setVerifier(''); }} disabled={busy}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      {failure && <p className="text-[11px] text-red-500">{failure}</p>}
    </div>
  );
};

/**
 * The per-broker live kill switch.
 *
 * LiveTradingGate requires BrokerConnection.LiveTradingEnabled (or, for a paired
 * terminal, TerminalAgentSession.LiveTradingEnabled), and both default to false. The
 * endpoints and the service call existed but nothing rendered them, so every newly
 * connected broker was permanently blocked from live orders through the UI no matter
 * how successfully it connected.
 *
 * Deliberately explicit rather than a quiet toggle: this is the switch that lets real
 * money move, so it states what it does and turning it ON asks first. Turning it OFF
 * never asks — the safe direction should never have friction.
 */
const LiveSwitch: React.FC<{
  provider: string;
  enabled: boolean;
  terminal?: boolean;
  onChanged: () => void;
}> = ({ provider, enabled, terminal = false, onChanged }) => {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const apply = async (next: boolean) => {
    if (next && !window.confirm(
      `Allow live orders on ${provider}?\n\n` +
      'Orders from strategies routed here will execute against your funded account with real money.'
    )) return;

    setBusy(true);
    setError(null);
    try {
      if (terminal) await svc.setTerminalLiveEnabled(provider, next);
      else await svc.setLiveEnabled(provider, next);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not change the switch');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`rounded-md border p-3 ${enabled ? 'border-red-500/40 bg-red-500/[0.04]' : 'border-border'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-sm font-medium">
            {enabled
              ? <ShieldAlert className="h-3.5 w-3.5 shrink-0 text-red-500" />
              : <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
            Live orders {enabled ? 'allowed' : 'blocked'}
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {enabled
              ? 'Strategies routed to this broker can place real-money orders.'
              : 'This broker is connected but will refuse every live order until this is on.'}
          </p>
        </div>
        <Button
          size="sm"
          variant={enabled ? 'destructive' : 'outline'}
          disabled={busy}
          onClick={() => void apply(!enabled)}
          className="shrink-0"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : enabled ? 'Block' : 'Allow'}
        </Button>
      </div>
      {error && <p className="mt-2 text-xs text-destructive">{error}</p>}
    </div>
  );
};

const StatusPill: React.FC<{ connection: BrokerConnection | null }> = ({ connection }) => {
  const status = connection?.status ?? 'Disconnected';
  const tone =
    status === 'Connected' ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
      : status === 'NeedsReauth' ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400'
        : status === 'Error' ? 'bg-red-500/15 text-red-600 dark:text-red-400'
          : 'bg-muted text-muted-foreground';
  const label =
    status === 'NeedsReauth' ? 'Reconnect required'
      : status === 'Pending' ? 'Not verified yet'
        : status;

  return <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${tone}`}>{label}</span>;
};

const CapabilitySummary: React.FC<{ broker: AvailableBroker }> = ({ broker }) => {
  const facts = [
    broker.assetClasses.join(' / '),
    broker.shorting ? 'shorting' : 'long only',
    broker.fractionalQuantity ? 'fractional' : 'whole units',
    broker.preflight ? 'preview supported' : null,
    broker.authoritativeOrderLookup ? null : 'best-effort order lookup',
  ].filter(Boolean) as string[];

  return (
    <div className="flex flex-wrap gap-1.5">
      {facts.map((fact) => (
        <span key={fact} className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
          {fact}
        </span>
      ))}
    </div>
  );
};

// ── Credentials / OAuth ─────────────────────────────────────────

const CredentialPanel: React.FC<BrokerCardProps> = ({ broker, environment, connection, onChanged }) => {
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setFailure(null);
    setMessage(null);
    try {
      await svc.upsert(broker.provider, environment, { apiKey, apiSecret });
      setApiKey('');
      setApiSecret('');
      setMessage('Credentials stored. Discover an account next.');
      await onChanged();
    } catch (e: any) {
      setFailure(e?.message || 'Failed to store credentials');
    } finally {
      setBusy(false);
    }
  };

  const connect = async () => {
    setBusy(true);
    setFailure(null);
    try {
      const { authorizeUrl } = await svc.startOAuth(
        broker.provider, environment, '/settings/brokers');
      window.location.href = authorizeUrl;
    } catch (e: any) {
      setFailure(e?.message || 'Failed to start the authorization');
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    setFailure(null);
    try {
      await svc.disconnect(broker.provider, environment);
      await onChanged();
    } catch (e: any) {
      setFailure(e?.message || 'Failed to disconnect');
    } finally {
      setBusy(false);
    }
  };

  // OAuth 1.0a (E*TRADE) fell through to the generic API-key form below, which
  // stores what is typed as ApiKey/ApiSecret. The factory needs the OAuth access
  // token in AccessToken and its secret in RefreshToken, so a connection made that
  // way could never authorise — the two-call flow existed in the service and nothing
  // called it.
  if (broker.authMode === 'OAuth1') {
    return <OAuth1Panel broker={broker} environment={environment} connection={connection} onChanged={onChanged} />;
  }

  if (broker.authMode === 'OAuth2') {
    return (
      <div className="space-y-2">
        {!broker.oauthConfigured && (
          <p className="text-[11px] text-amber-600 dark:text-amber-400">
            No {broker.provider} application is registered in this deployment yet, so the consent
            flow cannot start. The broker application has to be approved first.
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={connect} disabled={busy || !broker.oauthConfigured}>
            {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <ExternalLink className="mr-1 h-3.5 w-3.5" />}
            {connection?.hasCredentials ? 'Reconnect' : 'Connect'} {broker.provider}
          </Button>
          {connection?.hasCredentials && (
            <Button size="sm" variant="outline" onClick={disconnect} disabled={busy}>
              <Unlink className="mr-1 h-3.5 w-3.5" /> Disconnect
            </Button>
          )}
        </div>
        {failure && <p className="text-[11px] text-red-500">{failure}</p>}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="grid gap-2 sm:grid-cols-2">
        <label className="space-y-1">
          <span className="text-[11px] text-muted-foreground">API key</span>
          <input
            className="w-full rounded-md border bg-background px-2 py-1.5 text-xs"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder={connection?.hasCredentials ? 'stored — leave blank to keep' : ''}
            autoComplete="off"
          />
        </label>
        <label className="space-y-1">
          <span className="text-[11px] text-muted-foreground">API secret</span>
          <input
            type="password"
            className="w-full rounded-md border bg-background px-2 py-1.5 text-xs"
            value={apiSecret}
            onChange={(e) => setApiSecret(e.target.value)}
            placeholder={connection?.hasCredentials ? 'stored — leave blank to keep' : ''}
            autoComplete="off"
          />
        </label>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={save} disabled={busy}>
          {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <KeyRound className="mr-1 h-3.5 w-3.5" />}
          Save credentials
        </Button>
        {connection?.hasCredentials && (
          <Button size="sm" variant="outline" onClick={disconnect} disabled={busy}>
            <Unlink className="mr-1 h-3.5 w-3.5" /> Disconnect
          </Button>
        )}
      </div>
      {message && <p className="text-[11px] text-emerald-600 dark:text-emerald-400">{message}</p>}
      {failure && <p className="text-[11px] text-red-500">{failure}</p>}
    </div>
  );
};

// ── Account selection ───────────────────────────────────────────

const AccountPanel: React.FC<BrokerCardProps> = ({ broker, environment, connection, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const discover = async () => {
    setBusy(true);
    setFailure(null);
    try {
      await svc.discoverAccounts(broker.provider, environment);
      await onChanged();
    } catch (e: any) {
      setFailure(e?.message || 'Account discovery failed');
    } finally {
      setBusy(false);
    }
  };

  const select = async (accountId: string) => {
    setBusy(true);
    setFailure(null);
    try {
      await svc.selectAccount(broker.provider, environment, accountId);
      await onChanged();
    } catch (e: any) {
      setFailure(e?.message || 'Could not select that account');
    } finally {
      setBusy(false);
    }
  };

  const accounts = connection?.availableAccounts ?? [];

  return (
    <div className="space-y-2 rounded-md border p-2.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium">Routing account</span>
        <Button size="sm" variant="outline" onClick={discover} disabled={busy}>
          {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-1 h-3.5 w-3.5" />}
          Discover
        </Button>
      </div>

      {broker.multipleAccounts && !connection?.accountId && (
        <p className="text-[11px] text-amber-600 dark:text-amber-400">
          No account selected — orders will not route until one is chosen.
        </p>
      )}

      {accounts.length === 0
        ? <p className="text-[11px] text-muted-foreground">No accounts discovered yet.</p>
        : (
          <div className="space-y-1">
            {accounts.map((account) => {
              const selected = connection?.accountId === account.accountId;
              return (
                <button
                  key={account.accountId}
                  onClick={() => select(account.accountId)}
                  disabled={busy || selected}
                  className={`flex w-full items-center justify-between rounded-md border px-2 py-1.5 text-left text-xs ${
                    selected ? 'border-primary bg-primary/10' : 'hover:bg-accent'
                  }`}
                >
                  <span>
                    <span className="font-medium">{account.label || account.accountId}</span>
                    {account.label && (
                      <span className="ml-2 text-[10px] text-muted-foreground">{account.accountId}</span>
                    )}
                  </span>
                  {selected && <CheckCircle2 className="h-3.5 w-3.5 text-primary" />}
                </button>
              );
            })}
          </div>
        )}

      {failure && <p className="text-[11px] text-red-500">{failure}</p>}
    </div>
  );
};

// ── Test + conformance ──────────────────────────────────────────

const VerifyPanel: React.FC<{ broker: AvailableBroker; environment: TradingEnvironment }> = ({
  broker, environment,
}) => {
  const [busy, setBusy] = useState<'test' | 'conformance' | null>(null);
  const [testMessage, setTestMessage] = useState<string | null>(null);
  const [testOk, setTestOk] = useState(false);
  const [report, setReport] = useState<ConformanceReport | null>(null);
  const [allowFillable, setAllowFillable] = useState(false);
  const [symbol, setSymbol] = useState(broker.assetClasses.includes('Crypto') ? 'BTCUSD' : 'AAPL');

  const runTest = async () => {
    setBusy('test');
    try {
      const result = await svc.test(broker.provider, environment);
      setTestOk(result.success);
      setTestMessage(result.account
        ? `${result.message} Account ${result.account.accountNumber}, equity ${result.account.equity ?? '—'}.`
        : result.message);
    } catch (e: any) {
      setTestOk(false);
      setTestMessage(e?.message || 'Connection test failed');
    } finally {
      setBusy(null);
    }
  };

  const runConformance = async () => {
    setBusy('conformance');
    try {
      setReport(await svc.conformance(broker.provider, environment, {
        symbol,
        allowFillableOrder: allowFillable && environment === 'Paper',
      }));
    } catch (e: any) {
      setTestOk(false);
      setTestMessage(e?.message || 'Conformance run failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-2 rounded-md border p-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={runTest} disabled={busy !== null}>
          {busy === 'test' ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="mr-1 h-3.5 w-3.5" />}
          Test connection
        </Button>
        <input
          className="w-24 rounded-md border bg-background px-2 py-1 text-xs"
          value={symbol}
          onChange={(e) => setSymbol(e.target.value)}
          aria-label="Conformance symbol"
        />
        <Button size="sm" variant="outline" onClick={runConformance} disabled={busy !== null}>
          {busy === 'conformance' ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
          Run conformance suite
        </Button>
      </div>

      {environment === 'Paper' && (
        <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <input
            type="checkbox"
            checked={allowFillable}
            onChange={(e) => setAllowFillable(e.target.checked)}
          />
          Allow a real minimum-size fillable order (paper only) — proves fills and flattening.
        </label>
      )}

      {testMessage && (
        <p className={`text-[11px] ${testOk ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'}`}>
          {testMessage}
        </p>
      )}

      {report && (
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-xs font-medium">
            {report.passed
              ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
              : <AlertTriangle className="h-3.5 w-3.5 text-red-500" />}
            {report.passed ? 'Conformance passed' : 'Conformance found failures'}
            {!report.includedFillEvidence && (
              <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] text-amber-600 dark:text-amber-400">
                no fill evidence
              </span>
            )}
          </div>
          <div className="overflow-hidden rounded-md border">
            {report.checks.map((check) => (
              <div key={check.step} className="flex gap-2 border-b px-2 py-1 text-[11px] last:border-b-0">
                <span className="w-5 shrink-0 text-muted-foreground">{check.step}</span>
                <span className={`w-24 shrink-0 font-medium ${
                  check.outcome === 'Pass' ? 'text-emerald-600 dark:text-emerald-400'
                    : check.outcome === 'Fail' ? 'text-red-500'
                      : 'text-muted-foreground'
                }`}>
                  {check.outcome}
                </span>
                <span className="flex-1">
                  <span className="font-medium">{check.name}</span>
                  {check.detail && <span className="block text-muted-foreground">{check.detail}</span>}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── MetaTrader terminal agent ───────────────────────────────────

const TerminalAgentPanel: React.FC<{ provider: string; environment: TradingEnvironment }> = ({
  provider, environment,
}) => {
  const [status, setStatus] = useState<TerminalAgentStatus | null>(null);
  const [code, setCode] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setStatus(await svc.terminalStatus(provider, environment));
      setFailure(null);
    } catch (e: any) {
      setFailure(e?.message || 'Failed to read terminal status');
    }
  }, [provider, environment]);

  useEffect(() => { void refresh(); }, [refresh]);

  // The live switch lives HERE rather than on the card, because `status` is the only
  // place a terminal's LiveTradingEnabled is readable — there is no BrokerConnection
  // for this route. On the card it read a permanently-false value, so it could turn
  // real-money trading on and then never show it as on, nor offer to turn it off.
  const liveSwitch = environment === 'Live' && status?.paired ? (
    <LiveSwitch
      provider={provider}
      enabled={status.liveTradingEnabled}
      terminal
      onChanged={() => { void refresh(); }}
    />
  ) : null;

  // A terminal can go offline at any moment, and the route fails closed when it
  // does, so the status is polled rather than read once.
  useEffect(() => {
    const timer = window.setInterval(() => { void refresh(); }, 10000);
    return () => window.clearInterval(timer);
  }, [refresh]);

  const pair = async () => {
    setBusy(true);
    setFailure(null);
    try {
      const result = await svc.pairTerminal(provider, environment);
      setCode(result.code);
      await refresh();
    } catch (e: any) {
      setFailure(e?.message || 'Failed to create a pairing code');
    } finally {
      setBusy(false);
    }
  };

  const unpair = async () => {
    setBusy(true);
    setFailure(null);
    try {
      await svc.unpairTerminal(provider, environment);
      setCode(null);
      await refresh();
    } catch (e: any) {
      setFailure(e?.message || 'Failed to unpair');
    } finally {
      setBusy(false);
    }
  };

  const eaName = provider === 'MetaTrader4' ? 'TradeExecuteAgent.mq4' : 'TradeExecuteAgent.mq5';

  return (
    <div className="space-y-2 rounded-md border p-2.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium">Terminal pairing</span>
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
          status?.live ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
            : status?.paired ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400'
              : 'bg-muted text-muted-foreground'
        }`}>
          {status?.live ? 'Terminal live' : status?.paired ? 'Paired, not responding' : 'Not paired'}
        </span>
      </div>

      <ol className="list-decimal space-y-0.5 pl-4 text-[11px] text-muted-foreground">
        <li>Copy <code className="rounded bg-muted px-1">{eaName}</code> into your terminal's Experts folder and compile it.</li>
        <li>In Tools → Options → Expert Advisors, allow WebRequest for this site's address.</li>
        <li>Enable automated trading in the terminal toolbar.</li>
        <li>Attach the expert advisor to any chart and paste the pairing code below.</li>
      </ol>

      {status?.paired && (
        <div className="rounded-md bg-muted/50 px-2 py-1.5 text-[11px]">
          <div>Account <span className="font-medium">{status.accountLogin}</span> on {status.brokerServer}</div>
          <div className="text-muted-foreground">
            Executing broker: {status.brokerName || 'unknown'} ·{' '}
            automated trading {status.autoTradingEnabled ? 'enabled' : 'disabled'} ·{' '}
            last heartbeat {status.lastHeartbeatAt ? new Date(status.lastHeartbeatAt).toLocaleTimeString() : 'never'}
          </div>
        </div>
      )}

      {status?.paired && !status.live && (
        <p className="text-[11px] text-amber-600 dark:text-amber-400">
          The terminal is not responding. Orders are refused until it reconnects with automated
          trading enabled — a queued order that cannot execute is worse than a refused one.
        </p>
      )}

      {code && (
        <div className="flex items-center gap-2 rounded-md border border-primary/40 bg-primary/5 px-2 py-1.5">
          <span className="font-mono text-sm tracking-widest">{code}</span>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => void navigator.clipboard?.writeText(code)}
            title="Copy pairing code"
          >
            <Copy className="h-3.5 w-3.5" />
          </Button>
          <span className="text-[10px] text-muted-foreground">Valid for 10 minutes, single use.</span>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={pair} disabled={busy}>
          {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Link2 className="mr-1 h-3.5 w-3.5" />}
          {status?.paired ? 'Re-pair terminal' : 'Create pairing code'}
        </Button>
        {status?.paired && (
          <Button size="sm" variant="outline" onClick={unpair} disabled={busy}>
            <Unlink className="mr-1 h-3.5 w-3.5" /> Unpair
          </Button>
        )}
      </div>

      {liveSwitch}

      {failure && <p className="text-[11px] text-red-500">{failure}</p>}
    </div>
  );
};
